// MIXED: Production error page infrastructure + Template content
// KEEP: Error page structure, authentication integration, role display patterns
// REPLACE: Specific messaging, support contact information, branding

import React from 'react'
import type { JSX } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useAuth } from '@/hooks/useAppAuth'
import { useApi } from '@/hooks/useAppApi'

/**
 * PRODUCTION INFRASTRUCTURE: Unauthorized page component
 * 
 * Keep this error page structure and authentication integration:
 * - 403 error page layout and patterns
 * - User authentication context display
 * - Role information for debugging/support
 * - Navigation options and error recovery
 * - Professional error handling presentation
 * - Responsive design and accessibility
 * 
 * Customize the template content:
 * - Error messaging and explanations
 * - Support contact information (email, links)
 * - Branding and visual styling
 * - Navigation destinations and labels
 * - Action button text and behavior
 */
export const UnauthorizedPage = (): JSX.Element => {
  const { isAuthenticated } = useAuth()
  const { user } = useApi()
  const navigate = useNavigate()
  
  // Handle go back functionality
  const handleGoBack = (): void => {
    // If there's history, go back, otherwise go to dashboard or home
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate(isAuthenticated ? '/dashboard' : '/')
    }
  }
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-lg space-y-8">
        {/* Error Header */}
        <div className="text-center space-y-4">
          <div className="text-8xl font-bold text-destructive/20">403</div>
          <h1 className="text-3xl font-bold tracking-tight">{/* TEMPLATE CODE: Customize error title */}Access Denied</h1>
          <p className="text-muted-foreground">
            {/* TEMPLATE CODE: Customize error description */}
            You don't have permission to access this resource.
          </p>
        </div>
        
        {/* Main Content Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-center">{/* TEMPLATE CODE: Customize card title */}Insufficient Permissions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* User Information */}
            {isAuthenticated && user && (
              <div className="space-y-3">
                <div className="text-sm font-medium">Your Account:</div>
                <div className="bg-muted p-3 rounded-md space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Name:</span>
                    <span className="text-sm">{user.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Email:</span>
                    <span className="text-sm">{user.email}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Scopes:</span>
                    <div className="flex gap-1">
                      {user.scopes.map(role => (
                        <Badge key={role} variant="secondary" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Explanation */}
            <div className="space-y-3">
              <div className="text-sm font-medium">What happened?</div>
              <p className="text-sm text-muted-foreground">
                {/* TEMPLATE CODE: Customize explanation text */}
                The page you're trying to access requires higher privileges than your current account provides. 
                This is likely because:
              </p>
              <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1 ml-2">
                {/* TEMPLATE CODE: Customize permission explanation list */}
                <li>You need additional role permissions</li>
                <li>The resource is restricted to administrators</li>
                <li>Your account needs to be upgraded</li>
              </ul>
            </div>
            
            {/* Actions */}
            <div className="space-y-3">
              <div className="text-sm font-medium">What can you do?</div>
              <div className="grid gap-2">
                <Button onClick={handleGoBack} variant="default" className="w-full">
                  Go Back
                </Button>
                
                <div className="grid grid-cols-2 gap-2">
                  <Link to={isAuthenticated ? '/dashboard' : '/'}>
                    <Button variant="outline" size="sm" className="w-full">
                      {isAuthenticated ? 'Dashboard' : 'Home'}
                    </Button>
                  </Link>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      // TEMPLATE CODE: Replace with your actual support contact method
                      const email = 'support@revalizesoftware.com'
                      const subject = 'Access Request - Permission Denied'
                      const body = `Hi,\n\nI need access to a resource but received a 403 error.\n\nUser: ${user?.email || 'N/A'}\nCurrent Scopes: ${user?.scopes.join(', ') || 'None'}\n\nCould you please review my permissions?\n\nThanks!`
                      
                      window.location.href = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
                    }}
                  >
                    Request Access
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          {/* TEMPLATE CODE: Customize footer contact message */}
          If you believe this is an error, please contact your system administrator.
        </div>
      </div>
    </div>
  )
}
